import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const { resetPasswordForEmail } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await resetPasswordForEmail(email);
      if (error) throw error;
      setResetEmailSent(true);
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      {/* Background with image overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950"></div>
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{ 
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, 
          backgroundSize: '40px 40px' 
        }}></div>
        {/* Additional pattern */}
        <div className="absolute inset-0 opacity-5" style={{ 
          backgroundImage: `linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)`, 
          backgroundSize: '80px 80px' 
        }}></div>
      </div>
      
      {/* Animated overlay effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        <div className="absolute -top-40 -right-40 w-[30rem] h-[30rem] bg-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-[30rem] h-[30rem] bg-purple-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="w-full max-w-md z-20 relative">
        {/* Logo and Title */}
        <div className="text-center mb-10">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 mb-6 shadow-xl shadow-blue-500/30">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
            {t('auth.resetYourPassword')}
          </h1>
          <p className="text-slate-300 mt-2 text-lg">
            {t('auth.wellSendYouLink')}
          </p>
        </div>

        {/* Forgot Password Form */}
        <div className="bg-slate-800/60 backdrop-blur-md rounded-2xl p-8 border-2 border-blue-500/30 shadow-2xl">
          {error && (
            <div className="bg-red-600/30 border-2 border-red-600 rounded-xl p-4 flex items-center gap-3 mb-6 shadow-lg shadow-red-500/20">
              <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
              <span className="text-red-400">{error}</span>
            </div>
          )}

          {!resetEmailSent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm text-slate-400 mb-2">{t('auth.email')}</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.enterYourEmail')}
                    className="w-full bg-slate-900/80 text-white pl-10 pr-4 py-3 rounded-xl border border-slate-600/80 focus:outline-none focus:ring-2 focus:ring-blue-500/80 focus:border-blue-500/80 transition-all shadow-inner shadow-black/30"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-blue-500/50 mt-2 border border-blue-400/30"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  t('auth.resetPassword')
                )}
              </button>

              <Link
                to="/signin"
                className="w-full flex items-center justify-center gap-2 text-slate-400 hover:text-white transition-colors py-2"
              >
                <ArrowLeft size={16} />
                {t('auth.backToSignIn')}
              </Link>
            </form>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-green-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">{t('auth.checkYourEmail')}</h3>
              <p className="text-slate-300 mb-6">
                {t('auth.weveSentPasswordReset')} <span className="text-white font-medium">{email}</span>
              </p>
              <Link
                to="/signin"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/40 inline-block border border-blue-400/30"
              >
                {t('auth.returnToSignIn')}
              </Link>
              <p className="text-sm text-slate-400 mt-4">
                {t('auth.didntReceiveEmail')}{' '}
                <button
                  type="button"
                  onClick={() => setResetEmailSent(false)}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  {t('auth.tryAgain')}
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-8 text-center bg-slate-900/60 py-3 px-4 rounded-xl border border-blue-500/20 shadow-lg">
          <div className="flex items-center justify-center gap-2 text-slate-300 text-sm">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span>{t('auth.secureConnection')}</span>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute -bottom-10 -right-10 w-40 h-40 border border-blue-500/20 rounded-full"></div>
        <div className="absolute -top-5 -left-5 w-20 h-20 border border-purple-500/20 rounded-full"></div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;